import axios from "axios";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import DataTable from "../components/DataTable";

const MODULO = "LegalizacionViaticos";

const normalizar = (texto = "") =>
  String(texto)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, "")
    .toLowerCase();

const leerPermisos = () => {
  try {
    return JSON.parse(
      localStorage.getItem("permisos") ||
      localStorage.getItem("usuario") ||
      localStorage.getItem("user") ||
      localStorage.getItem("usuarios") ||
      "{}"
    );
  } catch {
    return {};
  }
};

const buscarModulo = (datos, nombreModulo) => {
  const modulos = Array.isArray(datos)
    ? datos
    : datos?.modulos ||
    datos?.permisos ||
    datos?.usuario?.modulos ||
    datos?.usuario?.permisos ||
    datos?.user?.modulos ||
    datos?.user?.permisos ||
    [];

  return (
    modulos.find((modulo) => {
      const nombre =
        modulo?.nombreModulo ||
        modulo?.modulo ||
        modulo?.nombre ||
        modulo?.moduloObjectId?.nombreModulo ||
        "";

      return normalizar(nombre) === normalizar(nombreModulo);
    }) || {}
  );
};

const formatearFecha = (fecha) => {
  if (!fecha) return "-";

  const partes = String(fecha).split("T")[0].split("-");

  if (partes.length !== 3) return "-";

  return `${partes[2]}/${partes[1]}/${partes[0]}`;
};

const ListarLegalizacionViaticos = ({ refresh }) => {
  const navigate = useNavigate();
  const { id: agendaId } = useParams();

  const [legalizaciones, setLegalizaciones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [eliminandoId, setEliminandoId] = useState("");

  const datosPermisos = leerPermisos();

  const rol = normalizar(
    datosPermisos?.rolObjectId?.nombreRol ||
    datosPermisos?.rol?.nombreRol ||
    datosPermisos?.rol?.nombre ||
    datosPermisos?.rol ||
    datosPermisos?.role ||
    datosPermisos?.usuario?.rol ||
    datosPermisos?.user?.rol ||
    ""
  );

  const esAdmin =
    rol.includes("superadmin") ||
    rol.includes("administrador");

  const modulo = buscarModulo(
    datosPermisos,
    MODULO
  );

  const puedeLeer =
    esAdmin ||
    modulo?.puedeLeer === true ||
    modulo?.leer === true ||
    modulo?.read === true ||
    modulo?.ver === true;

  const puedeCrear =
    esAdmin ||
    modulo?.puedeCrear === true ||
    modulo?.crear === true ||
    modulo?.create === true;

  const puedeActualizar =
    esAdmin ||
    modulo?.puedeActualizar === true ||
    modulo?.actualizar === true ||
    modulo?.editar === true ||
    modulo?.update === true;

  const puedeEliminar =
    esAdmin ||
    modulo?.puedeEliminar === true ||
    modulo?.eliminar === true ||
    modulo?.delete === true;

  const config = {
    headers: {
      Authorization: `Bearer ${localStorage.getItem("token") ||
        sessionStorage.getItem("token") ||
        ""
        }`,
    },
  };

  const validarSesion = (error) => {
    if (error?.response?.status !== 401) {
      return false;
    }

    Swal.fire({
      icon: "warning",
      title: "Sesión expirada",
      text: "Por favor inicia sesión nuevamente.",
      confirmButtonText: "Aceptar",
    }).then(() => {
      localStorage.removeItem("token");
      sessionStorage.removeItem("token");
      navigate("/login");
    });

    return true;
  };

  const cargarDatos = async () => {
    if (!puedeLeer || !agendaId) {
      setLegalizaciones([]);
      return;
    }

    try {
      setLoading(true);

      const { data } = await axios.get(
        `/api/legalizacionesviaticos/agenda/${agendaId}`,
        config
      );

      if (Array.isArray(data?.legalizaciones)) {
        setLegalizaciones(data.legalizaciones);
      } else if (data?.legalizacion) {
        setLegalizaciones([data.legalizacion]);
      } else {
        setLegalizaciones([]);
      }
    } catch (error) {
      if (validarSesion(error)) return;

      if (error?.response?.status === 404) {
        setLegalizaciones([]);
        return;
      }

      console.error(
        "Error cargando la legalización:",
        error
      );

      Swal.fire(
        "Error",
        error?.response?.data?.message ||
        "No se pudo cargar la legalización.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarDatos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agendaId, refresh]);

  const eliminarLegalizacion = async (
    legalizacionId
  ) => {
    if (!puedeEliminar) {
      Swal.fire(
        "Sin permisos",
        "No tiene permisos para eliminar legalizaciones.",
        "warning"
      );
      return;
    }

    const confirmacion = await Swal.fire({
      title: "¿Eliminar legalización?",
      text: "También se eliminarán las fotografías y asistencias.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#d33",
    });

    if (!confirmacion.isConfirmed) return;

    try {
      setEliminandoId(legalizacionId);

      const { data } = await axios.delete(
        `/api/legalizacionesviaticos/${legalizacionId}`,
        config
      );

      setLegalizaciones((actuales) =>
        actuales.filter(
          (item) => item._id !== legalizacionId
        )
      );

      Swal.fire({
        icon: "success",
        title:
          data?.message ||
          "Legalización eliminada",
        showConfirmButton: false,
        timer: 1500,
      });
    } catch (error) {
      if (validarSesion(error)) return;

      console.error(
        "Error eliminando la legalización:",
        error
      );

      Swal.fire(
        "Error",
        error?.response?.data?.message ||
        "No se pudo eliminar la legalización.",
        "error"
      );
    } finally {
      setEliminandoId("");
    }
  };

  const dataTabla = legalizaciones.map(
    (item, index) => {
      const agenda =
        item.agendaViaticoObjectId || {};

      const procesando =
        eliminandoId === item._id;

      return {
        item: index + 1,

        orden: (
          <span className="small fw-semibold">
            {item.numeroOrdenViaje ||
              "Sin número"}
          </span>
        ),

        agenda: (
          <div style={{ minWidth: 180 }}>
            <div className="fw-semibold">
              {agenda.entidad || "Sin entidad"}
            </div>

            {agenda.ruta && (
              <small className="text-secondary">
                {agenda.ruta}
              </small>
            )}
          </div>
        ),

        desplazamiento: (
          <div
            className="small"
            style={{ minWidth: 120 }}
          >
            <div>
              <span className="text-secondary">
                Inicio:
              </span>{" "}
              <strong>
                {formatearFecha(
                  agenda.fechaInicioDesplazamiento
                )}
              </strong>
            </div>

            <div>
              <span className="text-secondary">
                Fin:
              </span>{" "}
              <strong>
                {formatearFecha(
                  agenda.fechaFinDesplazamiento
                )}
              </strong>
            </div>
          </div>
        ),

        presentacion: (
          <div style={{ minWidth: 120 }}>
            <div className="fw-semibold">
              {item.ciudadPresentacion ||
                "Sin ciudad"}
            </div>

            <small className="text-secondary">
              {formatearFecha(
                item.fechaPresentacion
              )}
            </small>
          </div>
        ),

        acciones: (
          <>
            {/* ESCRITORIO */}
            <div className="d-none d-md-flex gap-2 justify-content-end">
              {puedeActualizar && (
                <button
                  type="button"
                  className="btn btn-warning btn-sm d-flex align-items-center justify-content-center"
                  style={{
                    width: 34,
                    height: 34,
                  }}
                  title="Editar"
                  disabled={loading || procesando}
                  onClick={() =>
                    navigate(
                      `/instructor/legalizacionviaticos/update/${item._id}`
                    )
                  }
                >
                  <i className="fa-solid fa-pen-to-square"></i>
                </button>
              )}

              {puedeEliminar && (
                <button
                  type="button"
                  className="btn btn-danger btn-sm d-flex align-items-center justify-content-center"
                  style={{
                    width: 34,
                    height: 34,
                  }}
                  title="Eliminar"
                  disabled={loading || procesando}
                  onClick={() =>
                    eliminarLegalizacion(item._id)
                  }
                >
                  <i
                    className={`fa-solid ${procesando
                      ? "fa-spinner fa-spin"
                      : "fa-trash"
                      }`}
                  ></i>
                </button>
              )}
            </div>

            {/* MÓVIL */}
            <div className="d-flex d-md-none gap-2">
              {puedeActualizar && (
                <button
                  type="button"
                  className="btn btn-outline-warning btn-sm d-flex flex-column align-items-center justify-content-center"
                  style={{
                    width: "50%",
                    minHeight: 55,
                  }}
                  disabled={loading || procesando}
                  onClick={() =>
                    navigate(
                      `/instructor/legalizacionviaticos/update/${item._id}`
                    )
                  }
                >
                  <i className="fa-solid fa-pen-to-square mb-1"></i>
                  <small>Editar</small>
                </button>
              )}

              {puedeEliminar && (
                <button
                  type="button"
                  className="btn btn-outline-danger btn-sm d-flex flex-column align-items-center justify-content-center"
                  style={{
                    width: "50%",
                    minHeight: 55,
                  }}
                  disabled={loading || procesando}
                  onClick={() =>
                    eliminarLegalizacion(item._id)
                  }
                >
                  <i
                    className={`fa-solid ${procesando
                      ? "fa-spinner fa-spin"
                      : "fa-trash"
                      } mb-1`}
                  ></i>
                  <small>Eliminar</small>
                </button>
              )}
            </div>
          </>
        ),
      };
    }
  );

  const columns = [
    { Header: "#", accessor: "item" },
    { Header: "Orden", accessor: "orden" },
    { Header: "Agenda", accessor: "agenda" },
    { Header: "Desplazamiento", accessor: "desplazamiento" },
    { Header: "Presentación", accessor: "presentacion" },
    { Header: "Acciones", accessor: "acciones" },
  ];

  if (!puedeLeer) {
    return (
      <div className="container-fluid px-2 px-md-4">
        <div className="alert alert-warning">
          <i className="fa-solid fa-lock me-2"></i>
          No tiene permisos para consultar las legalizaciones.
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="container-fluid px-2 px-md-4">
        <div className="bg-white border rounded-4 shadow-sm overflow-hidden">
          <div className="px-3 px-md-4 py-3 border-bottom bg-white">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
             <div>
                <h5 className="mt-2 mb-1 fw-semibold">
                  Legalización de Viáticos
                </h5>

                <small className="text-secondary">
                  Gestión de legalización de agendas de viajes.
                </small>
              </div>

              <div className="d-flex gap-2 flex-nowrap">
                <button
                  type="button"
                  className="btn btn-outline-dark"
                  disabled={loading}
                  onClick={() => navigate(-1)}
                >
                  <i className="fa-solid fa-arrow-rotate-left me-2"></i>
                  Volver
                </button>

                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  disabled={loading}
                  onClick={cargarDatos}
                >
                  <i
                    className={`fa-solid ${loading
                      ? "fa-spinner fa-spin"
                      : "fa-rotate"
                      } me-2`}
                  ></i>
                  Actualizar
                </button>

                {puedeCrear && agendaId && (
                  <button
                    type="button"
                    className="btn btn-dark"
                    disabled={
                      loading ||
                      legalizaciones.length > 0
                    }
                    onClick={() =>
                      navigate(
                        `/instructor/legalizacionviaticos/nuevo/${agendaId}`
                      )
                    }
                    title={
                      legalizaciones.length > 0
                        ? "Esta agenda ya tiene una legalización"
                        : "Nueva legalización"
                    }
                  >
                    <i className="fa-solid fa-plus me-2"></i>
                    Nuevo
                  </button>
                )}
              </div>
            </div>
          </div>

          <div>
            {loading ? (
              <div className="text-center text-secondary py-5">
                <div
                  className="spinner-border"
                  role="status"
                  aria-hidden="true"
                ></div>

                <div className="mt-3">Cargando legalización…</div>
              </div>
            ) : dataTabla.length > 0 ? (
              <div className="overflow-hidden">
                <DataTable columns={columns}data={dataTabla}/>
              </div>
            ) : (
              <div className="text-center text-secondary py-5">
                <div className="display-6 mb-2">📄</div>

                <div className="fw-semibold">
                  No hay una legalización registrada
                </div>

                <div className="small mb-3">
                  Esta agenda todavía no tiene una legalización.
                </div>

                {puedeCrear && agendaId && (
                  <button
                    type="button"
                    className="btn btn-dark"
                    onClick={() =>
                      navigate(
                        `/instructor/legalizacionviaticos/nuevo/${agendaId}`
                      )
                    }
                  >
                    <i className="fa-solid fa-plus me-2"></i>
                    Crear legalización
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListarLegalizacionViaticos;